import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const OPTIONS_LIMIT = 15;

const filterOptions = createFilterOptions({
  limit: OPTIONS_LIMIT,
  trim: true,
});

const filterOptionsParty = createFilterOptions({
  limit: OPTIONS_LIMIT,
  trim: true,
  matchFrom: "start",
});

function GridTopBar(props) {
  const { rowCount, title } = props;

  return (
    <GridToolbarContainer>
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f8fbff",
          borderBottom: "1px solid #dbe7f3",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={`Rows: ${rowCount}`}
          color="primary"
          variant="outlined"
          sx={{ height: 24 }}
        />
      </Box>
    </GridToolbarContainer>
  );
}

function PartEditAutocomplete(props) {
  const { id, field, value, api, allparts } = props;

  const selected =
    allparts.find((p) => p.itemcode === value || p.itemid === value) || null;

  return (
    <Autocomplete
      size="small"
      options={allparts}
      value={selected}
      autoHighlight
      getOptionLabel={(option) => option?.itemcode || ""}
      isOptionEqualToValue={(option, val) => option?.itemid === val?.itemid}
      onChange={async (event, newValue) => {
        const item = newValue || null;

        await api.setEditCellValue({
          id,
          field,
          value: item?.itemcode || "",
        });

        await api.setEditCellValue({
          id,
          field: "againstitemid",
          value: item?.itemid || null,
        });

        await api.setEditCellValue({
          id,
          field: "againstitemname",
          value: item?.itemname || "",
        });

        api.commitCellChange({ id, field });
        api.setCellMode(id, field, "view");
      }}
      renderOption={(props2, option) => (
        <li {...props2} key={option.itemid || option.itemcode}>
          <Box sx={{ width: "100%" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
              {option.itemcode} - {option.itemname}
            </Typography>
            <Typography
              sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.2 }}
            >
              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
              {option.rackname || "N/A"}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => <TextField {...params} autoFocus />}
    />
  );
}

const WarrantyClaim = () => {
  const userToken = LocalstorageHelper.GetUserToken();

  const [disableSave, setDisableSave] = useState(false);
  const [disableUpdate, setDisableUpdate] = useState(false);

  const [claimdate, setClaimDate] = useState(dayjs());
  const [claimno, setClaimNo] = useState("");
  const [mode, setMode] = useState(1);
  const [remarks, setRemarks] = useState("");

  const [searchdisabled, setSearchDisabled] = useState(true);
  const [searchbtndisabled, setSearchBtnDisabled] = useState(true);

  const [docopts, setDocOpts] = useState([]);
  const [docdetails, setDocDetails] = useState(null);

  const [partyopts, setPartyOpts] = useState([]);
  const [partydetails, setPartyDetails] = useState(null);

  const [allparts, setAllParts] = useState([]);
  const [unclaimedRows, setUnclaimedRows] = useState([]);
  const [claimRows, setClaimRows] = useState([]);

  const [loadingParts, setLoadingParts] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingUnclaimed, setLoadingUnclaimed] = useState(false);

  const [issueSearch, setIssueSearch] = useState("");
  const [expandedDocs, setExpandedDocs] = useState({});
  const [selectedIssueIds, setSelectedIssueIds] = useState({});

  const [manualPartInput, setManualPartInput] = useState("");
  const [manualPart, setManualPart] = useState(null);
  const [manualSrlNo, setManualSrlNo] = useState("");

  const manualPartRef = useRef(null);
  const manualSrlRef = useRef(null);

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
    return null;
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      LocalstorageHelper.RemoveUserToken();
    }, 3600000);

    return () => clearTimeout(timeout);
  }, []);

  const resetManualEntry = () => {
    setManualPartInput("");
    setManualPart(null);
    setManualSrlNo("");

    setTimeout(() => {
      if (manualPartRef.current) {
        manualPartRef.current.focus();
      }
    }, 50);
  };

  async function getPartyOpts() {
    try {
      const response = await fetch("/warranty/partydata", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setPartyOpts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load party list");
    }
  }

  async function getAllParts() {
    try {
      setLoadingParts(true);
      const response = await fetch("/warranty/issue/bat/allparts", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setAllParts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load parts");
    } finally {
      setLoadingParts(false);
    }
  }

  async function getDocNos() {
    try {
      const response = await fetch("/warranty/claim/documentnos", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setDocOpts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getPartyOpts();
    getAllParts();
    getDocNos();
  }, []);

  const handleModeChange = (e) => {
    const selectedMode = parseInt(e.target.value, 10);
    setMode(selectedMode);

    if (selectedMode === 1) {
      setSearchDisabled(true);
      setSearchBtnDisabled(true);
      setDocDetails(null);
      setClaimNo("");
      setClaimDate(dayjs());
      setPartyDetails(null);
      setClaimRows([]);
      setUnclaimedRows([]);
      setRemarks("");
      setIssueSearch("");
      setExpandedDocs({});
      setSelectedIssueIds({});
    } else {
      setSearchDisabled(false);
      setSearchBtnDisabled(false);
    }
  };

  const fetchPartByCode = async (code) => {
    try {
      const body = { code: String(code || "").trim() };
      if (!body.code) return null;

      const response = await fetch("/warranty/issue/bat/allparts/detailfetch", {
        method: "POST",
        headers: {
          sptoken: userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data?.type === "error" || !data?.itemid) return null;
      return data;
    } catch (err) {
      console.error(err.message);
      return null;
    }
  };

  const resolvePartFromInput = async (typedValue, selectedPart) => {
    if (selectedPart?.itemid) return selectedPart;

    const typed = String(typedValue || "").trim();
    if (!typed) return null;

    const localMatch = allparts.find(
      (item) =>
        String(item.itemcode || "").toUpperCase() === typed.toUpperCase()
    );

    if (localMatch) return localMatch;

    const fetched = await fetchPartByCode(typed);
    return fetched || null;
  };

  const fetchUnclaimedByParty = async (partyId) => {
    if (!partyId) {
      setUnclaimedRows([]);
      return;
    }

    try {
      setLoadingUnclaimed(true);

      const response = await fetch(`/warranty/claim/unclaimed/${partyId}`, {
        method: "GET",
        headers: { sptoken: userToken },
      });

      const jsonData = await response.json();
      setUnclaimedRows(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load unclaimed warranty issue details");
      setUnclaimedRows([]);
    } finally {
      setLoadingUnclaimed(false);
    }
  };

  const groupedUnclaimedDocs = useMemo(() => {
    const q = String(issueSearch || "").trim().toLowerCase();
    const map = new Map();

    for (const row of unclaimedRows) {
      const docno = row.warrantyissuedocumentno || "NO-DOC";

      if (!map.has(docno)) {
        map.set(docno, {
          documentno: docno,
          warrantyissueid: row.warrantyissueid,
          documentdate: row.documentdate,
          items: [],
        });
      }

      map.get(docno).items.push(row);
    }

    let docs = Array.from(map.values()).map((doc) => {
      let items = doc.items;

      if (q) {
        items = items.filter((row) => {
          return (
            String(row.warrantyissuedocumentno || "").toLowerCase().includes(q) ||
            String(row.againstitemcode || "").toLowerCase().includes(q) ||
            String(row.againstitemname || "").toLowerCase().includes(q) ||
            String(row.againstsrlno || "").toLowerCase().includes(q)
          );
        });
      }

      return {
        ...doc,
        items,
        itemcount: items.length,
      };
    });

    docs = docs.filter((doc) => doc.items.length > 0);
    docs.sort((a, b) => String(b.documentno).localeCompare(String(a.documentno)));

    return docs;
  }, [unclaimedRows, issueSearch]);

  const toggleDocExpand = (documentno) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [documentno]: !prev[documentno],
    }));
  };

  const isIssueRowAlreadyAdded = (issueRow) => {
    return claimRows.some(
      (r) => !r.manualflag && r.warrantyissuedtlid === issueRow.warrantyissuedtlid
    );
  };

  const addIssueRowToClaim = (selectedIssueRow) => {
    if (!selectedIssueRow) {
      toast.error("Select an issue detail first");
      return;
    }

    const alreadyExists = claimRows.some(
      (r) =>
        !r.manualflag &&
        r.warrantyissuedtlid === selectedIssueRow.warrantyissuedtlid
    );

    if (alreadyExists) {
      toast.error("This issue detail is already added");
      return;
    }

    const newRow = {
      srl: claimRows.length + 1,
      warrantyissuedtlid: selectedIssueRow.warrantyissuedtlid,
      warrantyissueid: selectedIssueRow.warrantyissueid,
      warrantyissuedocumentno: selectedIssueRow.warrantyissuedocumentno || "",
      againstitemid: selectedIssueRow.againstitemid || null,
      againstitemcode: selectedIssueRow.againstitemcode || "",
      againstitemname: selectedIssueRow.againstitemname || "",
      againstsrlno: selectedIssueRow.againstsrlno || "",
      manualflag: false,
    };

    setClaimRows((prev) => [...prev, newRow]);
  };

  const addWholeDocumentToClaim = (doc) => {
    const rowsToAdd = doc.items.filter((item) => !isIssueRowAlreadyAdded(item));

    if (!rowsToAdd.length) {
      toast.info("All items from this document are already added");
      return;
    }

    const confirmed = window.confirm(
      `Add all ${rowsToAdd.length} items from document ${doc.documentno}?`
    );

    if (!confirmed) return;

    setClaimRows((prev) => {
      const start = prev.length;
      const mapped = rowsToAdd.map((row, idx) => ({
        srl: start + idx + 1,
        warrantyissuedtlid: row.warrantyissuedtlid,
        warrantyissueid: row.warrantyissueid,
        warrantyissuedocumentno: row.warrantyissuedocumentno || "",
        againstitemid: row.againstitemid || null,
        againstitemcode: row.againstitemcode || "",
        againstitemname: row.againstitemname || "",
        againstsrlno: row.againstsrlno || "",
        manualflag: false,
      }));
      return [...prev, ...mapped];
    });

    toast.success(`${rowsToAdd.length} items added from ${doc.documentno}`);
  };

  const toggleIssueSelection = (issueRow) => {
    const key = String(issueRow.warrantyissuedtlid);
    setSelectedIssueIds((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const addSelectedCheckedItems = () => {
    const chosen = unclaimedRows.filter(
      (row) => selectedIssueIds[String(row.warrantyissuedtlid)]
    );

    const rowsToAdd = chosen.filter((item) => !isIssueRowAlreadyAdded(item));

    if (!rowsToAdd.length) {
      toast.info("No new selected items to add");
      return;
    }

    setClaimRows((prev) => {
      const start = prev.length;
      const mapped = rowsToAdd.map((row, idx) => ({
        srl: start + idx + 1,
        warrantyissuedtlid: row.warrantyissuedtlid,
        warrantyissueid: row.warrantyissueid,
        warrantyissuedocumentno: row.warrantyissuedocumentno || "",
        againstitemid: row.againstitemid || null,
        againstitemcode: row.againstitemcode || "",
        againstitemname: row.againstitemname || "",
        againstsrlno: row.againstsrlno || "",
        manualflag: false,
      }));
      return [...prev, ...mapped];
    });

    setSelectedIssueIds({});
    toast.success(`${rowsToAdd.length} selected items added`);
  };

  const getDocSelectionState = (doc) => {
    const ids = doc.items.map((x) => String(x.warrantyissuedtlid));
    const selectedCount = ids.filter((id) => selectedIssueIds[id]).length;

    if (selectedCount === 0) return "none";
    if (selectedCount === ids.length) return "all";
    return "partial";
  };

  const toggleWholeDocSelection = (doc) => {
    const ids = doc.items.map((x) => String(x.warrantyissuedtlid));
    const state = getDocSelectionState(doc);

    setSelectedIssueIds((prev) => {
      const next = { ...prev };

      if (state === "all") {
        ids.forEach((id) => {
          delete next[id];
        });
      } else {
        ids.forEach((id) => {
          next[id] = true;
        });
      }

      return next;
    });
  };

  const addManualRow = async () => {
    const finalManualPart = await resolvePartFromInput(
      manualPartInput,
      manualPart
    );

    if (!partydetails?.subledgerid) {
      toast.error("Select Party first");
      return;
    }

    if (!finalManualPart?.itemid) {
      toast.error("Select valid manual item");
      return;
    }

    if (!String(manualSrlNo || "").trim()) {
      toast.error("Manual serial no required");
      return;
    }

    const newRow = {
      srl: claimRows.length + 1,
      warrantyissuedtlid: null,
      warrantyissueid: null,
      warrantyissuedocumentno: "",
      againstitemid: finalManualPart.itemid || null,
      againstitemcode: finalManualPart.itemcode || "",
      againstitemname: finalManualPart.itemname || "",
      againstsrlno: manualSrlNo || "",
      manualflag: true,
    };

    setClaimRows((prev) => [...prev, newRow]);
    resetManualEntry();
  };

  const handleDeleteClick = (id) => () => {
    if (window.confirm("Are you sure you want to remove the item?")) {
      setClaimRows((prevRows) =>
        prevRows
          .filter((row) => row.srl !== id)
          .map((row, idx) => ({ ...row, srl: idx + 1 }))
      );
    }
  };

  const processRowUpdate = (newRow) => {
    const againstItem =
      allparts.find((p) => p.itemcode === newRow.againstitemcode) || null;

    if (!String(newRow.againstsrlno || "").trim()) {
      toast.error("Against serial required");
      return newRow;
    }

    const updatedRow = {
      ...newRow,
      againstitemid: againstItem?.itemid || newRow.againstitemid || null,
      againstitemcode: againstItem?.itemcode || newRow.againstitemcode || "",
      againstitemname: againstItem?.itemname || newRow.againstitemname || "",
    };

    setClaimRows((prev) =>
      prev.map((row) => (row.srl === updatedRow.srl ? updatedRow : row))
    );

    return updatedRow;
  };

  const claimColumns = useMemo(
    () => [
      {
        field: "action",
        type: "actions",
        headerClassName: "super-app-theme--header",
        headerName: "",
        width: 50,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(params.id)}
            color="inherit"
          />,
        ],
      },
      {
        field: "srl",
        headerName: "Srl",
        width: 55,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "warrantyissuedocumentno",
        headerName: "Issue Doc No",
        width: 150,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "againstitemcode",
        headerName: "Against Part",
        width: 160,
        editable: true,
        headerClassName: "super-app-theme--header",
        renderEditCell: (params) => (
          <PartEditAutocomplete {...params} allparts={allparts} />
        ),
      },
      {
        field: "againstitemname",
        headerName: "Description",
        width: 240,
        editable: false,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "againstsrlno",
        headerName: "Against Srl No",
        width: 150,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "manualflag",
        headerName: "Manual",
        width: 90,
        editable: false,
        headerClassName: "super-app-theme--header",
        valueGetter: (params) => (params.row.manualflag ? "Yes" : "No"),
      },
    ],
    [claimRows, allparts]
  );

  const handleSave = async (e) => {
    e.preventDefault();

    if (!partydetails?.subledgerid) {
      toast.error("Select Party");
      return;
    }

    if (!claimRows.length) {
      toast.error("Add at least one row");
      return;
    }

    for (const r of claimRows) {
      if (!String(r.againstitemcode || "").trim()) {
        toast.error(`Against part missing at row ${r.srl}`);
        return;
      }
      if (!String(r.againstsrlno || "").trim()) {
        toast.error(`Against serial missing at row ${r.srl}`);
        return;
      }
    }

    setDisableSave(true);
    setPageLoading(true);

    try {
      const body = {
        claimdate,
        partyid: partydetails?.subledgerid,
        remarks,
        rows: claimRows,
      };

      const response = await fetch("/warranty/claim/insert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify(body),
      });

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Save failed");
        setDisableSave(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Save failed");
      setDisableSave(false);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!docdetails?.claimheaderid) {
      toast.error("Select document");
      return;
    }

    setPageLoading(true);

    try {
      const response = await fetch(
        `/warranty/claim/data/${docdetails.claimheaderid}`,
        {
          method: "GET",
          headers: { sptoken: userToken },
        }
      );

      const jsonData = await response.json();

      if (jsonData.type === "error") {
        toast.error(jsonData.message || "Search failed");
        return;
      }

      setClaimRows(jsonData.detaildata || []);
      setClaimDate(dayjs(jsonData.headerdata.claimdate));
      setClaimNo(jsonData.headerdata.claimno || "");
      setPartyDetails({
        partydetails: jsonData.headerdata.partydetails,
        subledgerid: jsonData.headerdata.subledgerid,
      });
      setExpandedDocs({});
      setSelectedIssueIds({});
      fetchUnclaimedByParty(jsonData.headerdata.subledgerid);
    } catch (err) {
      console.log(err.message);
      toast.error("Search failed");
    } finally {
      setPageLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!docdetails?.claimheaderid) {
      toast.error("Select document");
      return;
    }

    if (!partydetails?.subledgerid) {
      toast.error("Select Party");
      return;
    }

    if (!claimRows.length) {
      toast.error("No rows to update");
      return;
    }

    for (const r of claimRows) {
      if (!String(r.againstitemcode || "").trim()) {
        toast.error(`Against part missing at row ${r.srl}`);
        return;
      }
      if (!String(r.againstsrlno || "").trim()) {
        toast.error(`Against serial missing at row ${r.srl}`);
        return;
      }
    }

    setDisableUpdate(true);
    setPageLoading(true);

    try {
      const body = {
        claimdate,
        partyid: partydetails?.subledgerid,
        remarks,
        rows: claimRows,
      };

      const response = await fetch(
        `/warranty/claim/update/${docdetails.claimheaderid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            sptoken: userToken,
          },
          body: JSON.stringify(body),
        }
      );

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Update failed");
        setDisableUpdate(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Update failed");
      setDisableUpdate(false);
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <>
      <Box>
        <Sidebar
          Title={"WARRANTY CLAIM"}
          expandedPanel={"panel2"}
          children={
            <Box component="form" sx={{ width: "100%" }} autoComplete="off">
              <Backdrop
                open={pageLoading || loadingParts || loadingUnclaimed}
                sx={{
                  color: "#fff",
                  zIndex: (theme) => theme.zIndex.drawer + 999,
                  backdropFilter: "blur(2px)",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={28} />
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                  {loadingUnclaimed
                    ? "Loading unclaimed issue details..."
                    : loadingParts
                    ? "Loading Parts..."
                    : "Processing..."}
                </Typography>
              </Backdrop>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: "10px 14px !important" }}>
                  <Stack
                    spacing={1}
                    direction={{ xs: "column", lg: "row" }}
                    alignItems={{ xs: "stretch", lg: "center" }}
                  >
                    <TextField
                      select
                      label="Mode"
                      value={mode}
                      onChange={handleModeChange}
                      sx={{ width: 110 }}
                      size="small"
                    >
                      <MenuItem value={1}>New</MenuItem>
                      <MenuItem value={2}>Edit</MenuItem>
                    </TextField>

                    <TextField
                      disabled
                      label="Claim No"
                      value={claimno}
                      sx={{ width: 140 }}
                      size="small"
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Claim Date"
                        inputFormat="DD/MM/YYYY"
                        value={claimdate}
                        onChange={(newValue) => {
                          setClaimDate(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 150 }}
                          />
                        )}
                      />
                    </LocalizationProvider>

                    <Autocomplete
                      id="party"
                      value={partydetails}
                      filterOptions={filterOptionsParty}
                      onChange={(event, newValue) => {
                        setPartyDetails(newValue);
                        setClaimRows([]);
                        setUnclaimedRows([]);
                        setIssueSearch("");
                        setExpandedDocs({});
                        setSelectedIssueIds({});
                        if (newValue?.subledgerid) {
                          fetchUnclaimedByParty(newValue.subledgerid);
                        }
                      }}
                      options={partyopts}
                      getOptionLabel={(option) => option.partydetails || ""}
                      sx={{ minWidth: 280, flex: 1 }}
                      renderInput={(params) => (
                        <TextField
                          required
                          {...params}
                          size="small"
                          label="Party"
                          placeholder="Party"
                        />
                      )}
                    />
                  </Stack>

                  <TextField
                    label="Remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ mt: 1.25 }}
                  />

                  {mode === 2 && (
                    <>
                      <Divider sx={{ my: 1.25 }} />
                      <Stack
                        spacing={1}
                        direction={{ xs: "column", lg: "row" }}
                        alignItems={{ xs: "stretch", lg: "center" }}
                      >
                        <Autocomplete
                          disabled={searchdisabled}
                          value={docdetails}
                          filterOptions={filterOptions}
                          onChange={(event, newValue) => setDocDetails(newValue)}
                          options={docopts}
                          getOptionLabel={(option) => option.claimno || ""}
                          sx={{ minWidth: 250, flex: 1 }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              placeholder="Search Claim"
                              label="Claim Document"
                            />
                          )}
                        />

                        <Button
                          disabled={searchbtndisabled}
                          variant="contained"
                          onClick={handleSearch}
                          startIcon={<SearchRoundedIcon />}
                          sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                        >
                          Search
                        </Button>
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: "10px 14px !important" }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DescriptionOutlinedIcon fontSize="small" color="primary" />
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        Unclaimed Warranty Issue Documents
                      </Typography>
                      <Chip
                        size="small"
                        label={`Docs: ${groupedUnclaimedDocs.length}`}
                        variant="outlined"
                      />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        label="Search"
                        placeholder="Doc / part / serial"
                        value={issueSearch}
                        onChange={(e) => setIssueSearch(e.target.value)}
                        sx={{ width: 260 }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={addSelectedCheckedItems}
                        sx={{ minWidth: 140 }}
                      >
                        Add Checked
                      </Button>
                    </Stack>
                  </Stack>

                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 360,
                      overflow: "auto",
                      borderRadius: 2,
                      bgcolor: "#fcfdff",
                    }}
                  >
                    {groupedUnclaimedDocs.length === 0 ? (
                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          No unclaimed warranty issue items found.
                        </Typography>
                      </Box>
                    ) : (
                      groupedUnclaimedDocs.map((doc) => {
                        const expanded = !!expandedDocs[doc.documentno];
                        const docSelectionState = getDocSelectionState(doc);
                        const alreadyAddedCount = doc.items.filter((x) =>
                          isIssueRowAlreadyAdded(x)
                        ).length;

                        return (
                          <Box
                            key={doc.documentno}
                            sx={{
                              borderBottom: "1px solid #edf2f7",
                              "&:last-child": { borderBottom: "none" },
                            }}
                          >
                            <Box
                              sx={{
                                px: 1,
                                py: 0.75,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                bgcolor: expanded ? "#f7fbff" : "transparent",
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => toggleDocExpand(doc.documentno)}
                              >
                                {expanded ? (
                                  <ExpandMoreRoundedIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowRightRoundedIcon fontSize="small" />
                                )}
                              </IconButton>

                              <Checkbox
                                size="small"
                                checked={docSelectionState === "all"}
                                indeterminate={docSelectionState === "partial"}
                                onChange={() => toggleWholeDocSelection(doc)}
                              />

                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                    {doc.documentno}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={`${doc.itemcount} item${
                                      doc.itemcount > 1 ? "s" : ""
                                    }`}
                                    sx={{ height: 22 }}
                                  />
                                  {alreadyAddedCount > 0 && (
                                    <Chip
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      label={`${alreadyAddedCount} already added`}
                                      sx={{ height: 22 }}
                                    />
                                  )}
                                </Stack>
                              </Box>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => addWholeDocumentToClaim(doc)}
                                sx={{ minWidth: 110 }}
                              >
                                Add Doc
                              </Button>
                            </Box>

                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 1.5, pb: 1 }}>
                                {doc.items.map((item) => {
                                  const checked =
                                    !!selectedIssueIds[String(item.warrantyissuedtlid)];
                                  const alreadyAdded = isIssueRowAlreadyAdded(item);

                                  return (
                                    <Box
                                      key={item.warrantyissuedtlid}
                                      sx={{
                                        px: 1,
                                        py: 0.75,
                                        ml: 4.5,
                                        mb: 0.5,
                                        borderRadius: 1.5,
                                        bgcolor: alreadyAdded ? "#f5f5f5" : "#ffffff",
                                        border: "1px solid #eef3f8",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={() => toggleIssueSelection(item)}
                                        disabled={alreadyAdded}
                                      />

                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                          sx={{
                                            fontSize: 12.5,
                                            fontWeight: 700,
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {item.againstitemcode} - {item.againstitemname}
                                        </Typography>
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                          flexWrap="wrap"
                                          useFlexGap
                                          sx={{ mt: 0.25 }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: 11.5,
                                              color: "text.secondary",
                                            }}
                                          >
                                            Serial: {item.againstsrlno}
                                          </Typography>
                                          {item.rackname && (
                                            <Typography
                                              sx={{
                                                fontSize: 11.5,
                                                color: "text.secondary",
                                              }}
                                            >
                                              Rack: {item.rackname}
                                            </Typography>
                                          )}
                                        </Stack>
                                      </Box>

                                      {alreadyAdded ? (
                                        <Chip
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          label="Added"
                                          sx={{ height: 22 }}
                                        />
                                      ) : (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          startIcon={<BoltRoundedIcon />}
                                          onClick={() => addIssueRowToClaim(item)}
                                          sx={{ minWidth: 90 }}
                                        >
                                          Add
                                        </Button>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })
                    )}
                  </Paper>
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: "10px 14px !important" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>
                    Manual Item Entry
                  </Typography>

                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", md: "center" }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Autocomplete
                      freeSolo
                      value={manualPart}
                      inputValue={manualPartInput}
                      filterOptions={filterOptions}
                      onInputChange={(event, newInputValue) => {
                        setManualPartInput(newInputValue);
                        if (!newInputValue) {
                          setManualPart(null);
                        }
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setManualPart(null);
                          setManualPartInput(newValue);
                        } else {
                          setManualPart(newValue);
                          setManualPartInput(newValue?.itemcode || "");
                        }
                      }}
                      options={allparts}
                      loading={loadingParts}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option?.itemcode || "";
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.itemid === value?.itemid
                      }
                      sx={{ width: 420 }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.itemid || option.itemcode}>
                          <Box sx={{ width: "100%" }}>
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                lineHeight: 1.2,
                              }}
                            >
                              {option.itemcode} - {option.itemname}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: "text.secondary",
                                lineHeight: 1.2,
                              }}
                            >
                              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
                              {option.rackname || "N/A"}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Manual Item"
                          placeholder="Type or search part"
                          size="small"
                          inputRef={manualPartRef}
                        />
                      )}
                    />

                    <TextField
                      label="Manual Serial No"
                      value={manualSrlNo}
                      onChange={(e) => setManualSrlNo(e.target.value)}
                      inputRef={manualSrlRef}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addManualRow();
                        }
                      }}
                      size="small"
                      sx={{ width: 180 }}
                    />

                    <Button
                      size="medium"
                      variant="contained"
                      color="secondary"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={addManualRow}
                      sx={{
                        borderRadius: 2,
                        px: 1.5,
                        height: 36,
                        minWidth: 120,
                      }}
                    >
                      Add Manual
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ borderRadius: 2.5, mb: 1.5 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      height: 420,
                      width: "100%",
                      "& .super-app-theme--header": {
                        backgroundColor: "#ebf5ff",
                        color: "#0d3557",
                        fontWeight: 800,
                        fontSize: 12,
                      },
                      "& .MuiDataGrid-cell": {
                        fontSize: 12,
                        py: 0.25,
                      },
                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                        {
                          outline: "1px solid #1976d2",
                        },
                    }}
                  >
                    <DataGrid
                      rows={claimRows}
                      columns={claimColumns}
                      getRowId={(row) => row.srl}
                      density="compact"
                      rowHeight={30}
                      headerHeight={32}
                      disableSelectionOnClick
                      editMode="cell"
                      processRowUpdate={processRowUpdate}
                      onProcessRowUpdateError={(error) => {
                        console.error(error);
                        toast.error("Failed to update row");
                      }}
                      components={{
                        Toolbar: () => (
                          <GridTopBar
                            rowCount={claimRows.length}
                            title="Warranty Claim Rows"
                          />
                        ),
                      }}
                      initialState={{
                        sorting: {
                          sortModel: [{ field: "srl", sort: "asc" }],
                        },
                        pagination: {
                          pageSize: 10,
                        },
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={1} justifyContent="center">
                {mode === 1 && (
                  <Button
                    disabled={disableSave}
                    onClick={handleSave}
                    size="medium"
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                  >
                    Save
                  </Button>
                )}

                {mode === 2 && (
                  <Button
                    disabled={disableUpdate}
                    onClick={handleUpdate}
                    size="medium"
                    variant="contained"
                    color="warning"
                    sx={{ borderRadius: 2, minWidth: 110, height: 36 }}
                  >
                    Update
                  </Button>
                )}
              </Stack>
            </Box>
          }
        />
        {redirectToLoginPage()}
      </Box>
    </>
  );
};

export default WarrantyClaim;